import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "@ag-grid-community/react";
import { ModuleRegistry } from "@ag-grid-community/core";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { CsvExportModule } from "@ag-grid-community/csv-export";
import { MasterDetailModule } from "@ag-grid-enterprise/master-detail";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { RichSelectModule } from "@ag-grid-enterprise/rich-select";
import HaqtButton from "../HaqtButton/HaqtButton";
import { IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { AppContext } from "../../context/AppContextProvider";
import { useContext } from "react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import "./HaqtGrid.scss";
import { getSessionStorageItem } from "../../utils/sessionStorageUtils";

// Register Ag-Grid modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  CsvExportModule,
  MenuModule,
  ExcelExportModule,
  MasterDetailModule,
  SetFilterModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  RichSelectModule,
]);

const HaqtGrid = ({
  rowData,
  columnDefs,
  pagination,
  pageSize,
  pageSizeSelector,
  gridApiRef,
  masterDetail,
  detailCellRenderer,
  gridOptions,
  gridClassName = "grid-dimenssion",
  isExport,
  getRows,
  sheetName,
  fileName,
  suppressContextMenu,
  gridBackground,
  domLayout,
  detailRowAutoHeight,
  onCellValueChanged,
  detailCellRendererParams,
  suppressCellSelection = false,
  frameworkComponents,
  tableTitleVisible = false,
  tableTitle,
}) => {
  //  default column definition
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
    };
  }, []);

  const { user } = useContext(AppContext);
  const systemName = getSessionStorageItem("haqt-selected-system")?.system
    ?.name;

  const currentDate = new Date(); // Get current date and time
  const dateValue = currentDate.toLocaleDateString(); // Get current date
  const timeValue = currentDate.toLocaleTimeString(); // Get current time
  const excelStyles = [
    {
      // Unique identifier for the style
      id: "header",

      // Alignment settings for the cell content
      alignment: {
        vertical: "Center", // Vertical alignment of the content
      },

      // Interior settings for the cell background
      interior: {
        color: "#d3d3d3", // Background color of the cell
        pattern: "Solid", // Fill pattern of the cell
        patternColor: undefined, // Color of the fill pattern (if applicable)
      },

      // Font settings for the cell text
      font: {
        color: "black", // Color of the text
        bold: true, // Whether the text is bold or not
      },
    },
  ];

  // Function to dynamically generate additional data for the Haqt-grid Excel file
  const createExcelSummary = () => {
    return [
      { cells: [] }, // Empty row
      {
        cells: [
          // Row with user name
          {
            data: {
              value: "User Name:",
              type: "String",
            },
          },
          {
            data: {
              value: user.userName,
              type: "String",
            },
          },
        ],
      },
      {
        cells: [
          // Row with date value
          {
            data: {
              value: "Date:",
              type: "String",
            },
          },
          {
            data: {
              value: dateValue,
              type: "String",
            },
          },
        ],
      },
      {
        cells: [
          // Row with time value
          {
            data: {
              value: "Time:",
              type: "String",
            },
          },
          {
            data: {
              value: timeValue,
              type: "String",
            },
          },
        ],
      },
      { cells: [] }, // Empty row
    ];
  };

  // Callback function for exporting data to Excel
  const onBtnExport = useCallback(() => {
    const actionColumnExists = columnDefs.some(
      (colDef) => colDef.headerName === "Action",
    );
    const filteredColDefs = actionColumnExists
      ? columnDefs.filter((colDef) => colDef.headerName !== "Action")
      : columnDefs;

    gridApiRef.current?.api?.exportDataAsExcel({
      // Dheeraj Edit - remvoing system name from file & sheet name
      // sheetName: `${systemName}_${sheetName}`,
      // fileName: `${systemName}_${fileName}`,
      sheetName: `${sheetName}`,
      fileName: `${fileName}`,
      prependContent: createExcelSummary(),
      columnKeys: filteredColDefs.map((colDef) => colDef.field), // Use columnKeys to specify columns
    });
  }, [columnDefs, gridApiRef, sheetName, fileName, createExcelSummary]);

  // Memoized default CSV export parameters
  const defaultCsvExportParams = useMemo(() => {
    return {
      getCustomContentBelowRow: (params) => {
        const rows = getRows(params);
        return rows.map((row) => row.cells);
      },
    };
  }, []);

  // default Excel export parameters
  // the file name can be changed as per the requirement
  const defaultExcelExportParams = useMemo(() => {
    return {
      getCustomContentBelowRow: (params) => getRows(params), // Function to get custom content below each row
      columnWidth: 120, // Default column width in the exported Excel file
    };
  }, []);
  const popupParent = useMemo(() => {
    return document.body;
  }, []);
  return (
    <div className="haqt-grid">
      <div
        className={
          !tableTitleVisible ? "haqt-grid-button" : "haqt-grid-button-title"
        }
      >
        {/* Export button */}
        {tableTitleVisible && <h4>{tableTitle}</h4>}
        {isExport && (
          <Tooltip title="Export">
            <IconButton
              className="grid-export-button"
              onClick={() => onBtnExport()}
            >
              <FileDownloadIcon
                className="haqt-export-icon"
                alt="File Download Icon"
                title="Export File"
              />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Grid dimensions container */}
      <div className={gridClassName}>
        <div
          className={"ag-theme-quartz grid-container"}
          style={gridBackground}
        >
          {/* Ag-Grid React component */}
          <AgGridReact
            ref={gridApiRef}
            rowData={rowData}
            columnDefs={columnDefs}
            reactiveCustomComponents
            defaultColDef={defaultColDef}
            pagination={pagination}
            paginationPageSize={pageSize}
            paginationPageSizeSelector={pageSizeSelector}
            masterDetail={masterDetail}
            detailCellRenderer={detailCellRenderer}
            gridOptions={gridOptions}
            rowSelection="multiple"
            popupParent={popupParent}
            defaultCsvExportParams={defaultCsvExportParams}
            defaultExcelExportParams={getRows && defaultExcelExportParams}
            suppressContextMenu={suppressContextMenu}
            domLayout={domLayout}
            detailRowAutoHeight={detailRowAutoHeight}
            onCellValueChanged={onCellValueChanged}
            detailCellRendererParams={detailCellRendererParams}
            suppressCellSelection={suppressCellSelection}
            frameworkComponents={frameworkComponents}
            excelStyles={excelStyles}
          />
        </div>
      </div>
    </div>
  );
};

HaqtGrid.propTypes = {
  rowData: PropTypes.array.isRequired,
  columnDefs: PropTypes.array.isRequired,
  pagination: PropTypes.bool,
  pageSize: PropTypes.number,
  pageSizeSelector: PropTypes.arrayOf(PropTypes.number),
  gridApiRef: PropTypes.object,
  masterDetail: PropTypes.bool,
  detailCellRenderer: PropTypes.func,
  gridOptions: PropTypes.object,
  isExport: PropTypes.bool,
  sheetName: PropTypes.string,
  fileName: PropTypes.string,
  suppressContextMenu: PropTypes.bool,
  detailRowAutoHeight: PropTypes.bool,
};
HaqtGrid.defaultProps = {
  pagination: true,
  pageSize: 25,
  pageSizeSelector: [10, 25, 50, 100],
  masterDetail: false,
  gridOptions: {},
  isExport: true,
  sheetName: "Mizu-Link",
  fileName: "Haqt-MizuLink.xlsx",
  suppressContextMenu: true,
  detailRowAutoHeight: false,
};
export default HaqtGrid;
